import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, MapPin, Phone, Mail, ShieldCheck, Leaf, BadgeCheck } from "lucide-react";
import { Breadcrumbs, CheckList, FAQAccordion, Reveal, Rise, Eyebrow, ArrowCard } from "@/components/chrome";
import { SITE } from "@/data/site";
import { SERVICES_FR } from "@/data/servicesFr";
import { SERVICES } from "@/data/services";
import { findProvince, findCity, paths } from "@/lib/seo";
import { serviceCityContentFr, cityContentFr } from "@/lib/contentFr";
import NotFound from "@/pages/NotFound";

const QC = findProvince("quebec");
const EASE = [0.22, 1, 0.36, 1];

function FrButtons({ prefix = "fr-cta", className = "" }) {
  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`}>
      <a href={SITE.phoneHref} data-testid={`${prefix}-call-button`}
        className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-cyan-700">
        <Phone className="h-4 w-4" /> Appelez le {SITE.phone}
      </a>
      <a href={SITE.emailHref} data-testid={`${prefix}-email-button`}
        className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-700 transition-colors hover:border-cyan-600 hover:text-cyan-700">
        <Mail className="h-4 w-4" /> {SITE.email}
      </a>
    </div>
  );
}

function FrHero({ crumbs, eyebrow, title, lead, prefix }) {
  return (
    <header className="border-b border-slate-200 bg-white">
      <div className="mx-auto max-w-6xl px-5 py-12 sm:py-16">
        <Breadcrumbs items={crumbs} />
        {eyebrow && <Eyebrow className="mt-6">{eyebrow}</Eyebrow>}
        <h1 data-testid={`${prefix}-title`}
          className="mt-3 max-w-3xl font-display text-4xl font-bold leading-[1.08] tracking-tight text-slate-900 sm:text-5xl">
          {title}
        </h1>
        {lead && <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">{lead}</p>}
        <FrButtons className="mt-8" prefix={prefix} />
      </div>
    </header>
  );
}

function FrCTABand({ title = "Un appel. Impeccable.", lead, prefix = "fr-cta-band" }) {
  return (
    <section className="mx-auto max-w-6xl px-5 py-14 sm:py-16" data-testid={prefix}>
      <div className="relative overflow-hidden rounded-3xl bg-slate-900 px-8 py-12 sm:px-14 sm:py-16">
        <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-cyan-500/20 blur-3xl" />
        <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-cyan-300">Soumissions gratuites le jour même</p>
        <h2 className="mt-3 max-w-xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">{title}</h2>
        {lead && <p className="mt-4 max-w-lg leading-relaxed text-slate-300">{lead}</p>}
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <a href={SITE.phoneHref} data-testid={`${prefix}-call-button`}
            className="inline-flex items-center gap-2 rounded-full bg-cyan-400 px-6 py-3 text-sm font-semibold text-slate-950 transition-colors hover:bg-cyan-300">
            <Phone className="h-4 w-4" /> Appelez le {SITE.phone}
          </a>
          <a href={SITE.emailHref} data-testid={`${prefix}-email-button`}
            className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3 text-sm font-semibold text-white transition-colors hover:border-cyan-300 hover:text-cyan-300">
            <Mail className="h-4 w-4" /> {SITE.email}
          </a>
        </div>
      </div>
    </section>
  );
}

const iconFor = (slug) => SERVICES.find((s) => s.slug === slug)?.icon;

export function FrHome() {
  return (
    <div data-testid="fr-home-page">
      <section className="relative overflow-hidden border-b border-slate-200 bg-white">
        <div className="dot-grid pointer-events-none absolute inset-0" />
        <div className="pointer-events-none absolute -top-32 right-0 h-96 w-96 rounded-full bg-cyan-100/60 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 py-20 sm:py-28">
          <motion.span initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-4 py-1.5 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-cyan-800"
            data-testid="fr-hero-badge">
            <MapPin className="h-3.5 w-3.5" /> {QC.cities.length} villes du Québec desservies
          </motion.span>
          <h1 className="mt-8 font-display text-4xl font-extrabold leading-[1.04] tracking-tight text-slate-900 sm:text-6xl lg:text-7xl" data-testid="fr-hero-title">
            {["Des espaces impeccables.", "Partout au Québec."].map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span className="block" initial={{ y: "112%" }} animate={{ y: "0%" }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.15, ease: EASE }}>
                  {i === 1 ? <span className="text-cyan-600">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.7 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg" data-testid="fr-hero-subtitle">
            Tbay Cleaning Services est le réseau canadien de nettoyage professionnel — entretien ménager, nettoyage
            commercial, grand nettoyage et neuf autres services spécialisés, par des équipes locales assurées.
            Sans formulaire. Sans attente.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7 }}>
            <FrButtons className="mt-9" prefix="fr-hero" />
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-500" data-testid="fr-hero-trust">
            <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-cyan-600" /> Assuré et cautionné</span>
            <span className="flex items-center gap-2"><BadgeCheck className="h-4 w-4 text-cyan-600" /> Équipes vérifiées</span>
            <span className="flex items-center gap-2"><Leaf className="h-4 w-4 text-cyan-600" /> Produits écologiques</span>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 sm:py-24" data-testid="fr-services-section">
        <Reveal>
          <Eyebrow>Nos services</Eyebrow>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-5xl">12 services spécialisés</h2>
        </Reveal>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES_FR.map((s, i) => (
            <Reveal key={s.slug} delay={(i % 3) * 0.06}>
              <ArrowCard to={`/fr/services/${s.slug}`} icon={iconFor(s.slug)} title={s.name} desc={s.tagline}
                testid={`fr-service-card-${s.slug}`} />
            </Reveal>
          ))}
        </div>
      </section>

      <section id="villes" className="border-y border-slate-200 bg-white" data-testid="fr-cities-section">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:py-24">
          <Reveal>
            <Eyebrow>Villes desservies</Eyebrow>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-5xl">
              Le nettoyage professionnel, partout au Québec
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {QC.cities.map((c, i) => (
              <Reveal key={c.slug} delay={(i % 4) * 0.04}>
                <Link to={`/fr/villes/${c.slug}`} data-testid={`fr-city-card-${c.slug}`}
                  className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-[#FAFAFC] p-6 transition-all duration-300 hover:-translate-y-1 hover:border-cyan-600/40">
                  <div className="flex items-center justify-between">
                    <MapPin className="h-4 w-4 text-cyan-700" />
                    <ArrowUpRight className="h-4 w-4 text-slate-300 transition-colors group-hover:text-cyan-600" />
                  </div>
                  <h3 className="mt-4 font-display text-lg font-semibold tracking-tight text-slate-900">{c.name}</h3>
                  <p className="mt-1.5 text-sm text-slate-500">Population {c.pop} · 12 services offerts</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <FrCTABand lead="Parlez à un vrai coordonnateur aujourd'hui — la plupart des soumissions arrivent en moins d'une heure ouvrable." prefix="fr-home-cta" />
    </div>
  );
}

export function FrServices() {
  return (
    <div data-testid="fr-services-page">
      <FrHero prefix="fr-services"
        crumbs={[{ name: "Accueil", path: "/fr" }, { name: "Services" }]}
        eyebrow="Tous nos services"
        title="12 services de nettoyage professionnels au Québec"
        lead="De l'entretien ménager hebdomadaire au nettoyage après construction — chaque service est livré par des équipes locales assurées et vérifiées, partout au Québec. Prix fixes par téléphone ou courriel." />
      <section className="mx-auto max-w-7xl px-5 py-16 sm:py-20">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES_FR.map((s, i) => (
            <Reveal key={s.slug} delay={(i % 3) * 0.06}>
              <ArrowCard to={`/fr/services/${s.slug}`} icon={iconFor(s.slug)} title={s.name} desc={s.tagline}
                testid={`fr-services-card-${s.slug}`} />
            </Reveal>
          ))}
        </div>
      </section>
      <FrCTABand lead="Pas certain du service qu'il vous faut? Décrivez votre espace — on vous conseille, sans frais." prefix="fr-services-cta" />
    </div>
  );
}

export function FrService() {
  const { serviceSlug } = useParams();
  const service = SERVICES_FR.find((s) => s.slug === serviceSlug);
  if (!service) return <NotFound />;
  const short = service.short.toLowerCase();

  return (
    <div data-testid="fr-service-page">
      <FrHero prefix="fr-service"
        crumbs={[{ name: "Accueil", path: "/fr" }, { name: "Services", path: "/fr/services" }, { name: service.name }]}
        eyebrow="Service professionnel"
        title={`${service.name} — partout au Québec`}
        lead={service.tagline} />

      <section className="border-b border-slate-200 bg-white" data-testid="fr-service-included">
        <div className="mx-auto max-w-6xl px-5 py-14 sm:py-16">
          <Rise><Eyebrow>La liste de contrôle</Eyebrow>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Ce qui est inclus à chaque visite
            </h2></Rise>
          <div className="mt-8"><CheckList items={service.includes} testid="fr-service-checklist" /></div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-14 sm:py-16" data-testid="fr-service-cities">
        <Rise><Eyebrow>Disponibilité</Eyebrow>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            {service.name} dans {QC.cities.length} villes du Québec
          </h2></Rise>
        <div className="mt-8 flex flex-wrap gap-x-5 gap-y-2.5 text-sm">
          {QC.cities.map((c) => (
            <Link key={c.slug} to={`/fr/villes/${c.slug}/${service.slug}`} data-testid={`fr-service-city-${c.slug}`}
              className="inline-flex items-center gap-1.5 text-slate-600 transition-colors hover:text-cyan-700">
              {service.short} à {c.name} <ArrowRight className="h-3 w-3 text-slate-300" />
            </Link>
          ))}
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white" data-testid="fr-service-faq-section">
        <div className="mx-auto max-w-6xl px-5 py-14 sm:py-16">
          <Rise><Eyebrow>FAQ</Eyebrow>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              {service.name} — questions fréquentes
            </h2></Rise>
          <div className="mt-8 max-w-3xl">
            <FAQAccordion testid="fr-service-faq" faqs={service.faqs.map((f) => ({
              q: f.q.replaceAll("{ville}", "votre ville"),
              a: f.a.replaceAll("{ville}", "votre ville").replaceAll("{phone}", SITE.phone).replaceAll("{email}", SITE.email),
            }))} />
          </div>
        </div>
      </section>

      <FrCTABand title={`Réservez votre ${short} au Québec`}
        lead="Un appel à un vrai coordonnateur. Prix fixe ferme, généralement en moins d'une heure ouvrable."
        prefix="fr-service-cta" />
    </div>
  );
}

export function FrCity() {
  const { citySlug } = useParams();
  const city = findCity("quebec", citySlug);
  if (!city) return <NotFound />;
  const c = cityContentFr(city, QC.cities);

  return (
    <div data-testid="fr-city-page">
      <FrHero prefix="fr-city"
        crumbs={[{ name: "Accueil", path: "/fr" }, { name: "Villes", path: "/fr#villes" }, { name: city.name }]}
        eyebrow={`Québec — population ${city.pop}`}
        title={c.h1}
        lead={`12 services de nettoyage professionnels à ${city.name}, par des équipes locales assurées. Soumissions à prix fixe par téléphone ou courriel — généralement en moins d'une heure ouvrable.`} />

      <section className="mx-auto max-w-6xl px-5 py-14 sm:py-16" data-testid="fr-city-overview">
        <div className="max-w-3xl space-y-5">
          {c.intro.map((p, i) => <p key={i} className="leading-relaxed text-slate-600 sm:text-lg">{p}</p>)}
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white" data-testid="fr-city-services">
        <div className="mx-auto max-w-6xl px-5 py-14 sm:py-16">
          <Rise><Eyebrow>Services à {city.name}</Eyebrow>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Tous les services de nettoyage à {city.name}
            </h2></Rise>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES_FR.map((s) => (
              <ArrowCard key={s.slug} to={`/fr/villes/${city.slug}/${s.slug}`} icon={iconFor(s.slug)}
                title={`${s.short} à ${city.name}`} desc={s.tagline} testid={`fr-city-service-${s.slug}`} />
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-14 sm:py-16" data-testid="fr-city-nearby">
        <Rise><Eyebrow>Secteurs voisins</Eyebrow>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            Aussi desservi près de {city.name}
          </h2></Rise>
        <div className="mt-8 flex flex-wrap gap-x-5 gap-y-2.5 text-sm">
          {c.nearby.map((n) => (
            <Link key={n.slug} to={`/fr/villes/${n.slug}`} data-testid={`fr-city-nearby-${n.slug}`}
              className="inline-flex items-center gap-1.5 text-slate-600 transition-colors hover:text-cyan-700">
              Nettoyage à {n.name} <ArrowRight className="h-3 w-3 text-slate-300" />
            </Link>
          ))}
        </div>
      </section>

      <section className="border-t border-slate-200 bg-white" data-testid="fr-city-faq-section">
        <div className="mx-auto max-w-6xl px-5 py-14 sm:py-16">
          <Rise><Eyebrow>FAQ</Eyebrow>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Nettoyage à {city.name} — questions fréquentes
            </h2></Rise>
          <div className="mt-8 max-w-3xl"><FAQAccordion faqs={c.faqs} testid="fr-city-faq" /></div>
        </div>
      </section>

      <FrCTABand title={`Réservez un nettoyage à ${city.name} aujourd'hui`}
        lead={`Appelez ou écrivez — un vrai coordonnateur prépare votre soumission pour ${city.name}, généralement en moins d'une heure ouvrable.`}
        prefix="fr-city-cta" />
    </div>
  );
}

export function FrServiceCity() {
  const { citySlug, serviceSlug } = useParams();
  const city = findCity("quebec", citySlug);
  const service = SERVICES_FR.find((s) => s.slug === serviceSlug);
  if (!city || !service) return <NotFound />;
  const c = serviceCityContentFr(service, city, QC.cities);
  const short = service.short.toLowerCase();

  return (
    <div data-testid="fr-service-city-page">
      <FrHero prefix="fr-service-city"
        crumbs={[{ name: "Accueil", path: "/fr" }, { name: "Villes", path: "/fr#villes" }, { name: city.name, path: `/fr/villes/${city.slug}` }, { name: service.name }]}
        eyebrow={`${city.name}, QC`}
        title={c.h1} lead={service.tagline} />

      <section className="mx-auto max-w-6xl px-5 py-14 sm:py-16" data-testid="fr-sc-intro">
        <div className="max-w-3xl space-y-5">
          {c.intro.map((p, i) => <p key={i} className="leading-relaxed text-slate-600 sm:text-lg">{p}</p>)}
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white" data-testid="fr-sc-included">
        <div className="mx-auto max-w-6xl px-5 py-14 sm:py-16">
          <Rise><Eyebrow>La liste de contrôle</Eyebrow>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Ce qui est inclus — {short} à {city.name}
            </h2></Rise>
          <div className="mt-8"><CheckList items={service.includes} testid="fr-sc-checklist" /></div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-14 sm:py-16" data-testid="fr-sc-local">
        <Rise><Eyebrow>Couverture locale</Eyebrow>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            {city.name} et les communautés voisines
          </h2></Rise>
        <div className="mt-6 max-w-3xl space-y-5">
          {c.local.map((p, i) => <p key={i} className="leading-relaxed text-slate-600">{p}</p>)}
        </div>
        <div className="mt-8 flex flex-wrap gap-x-5 gap-y-2.5 text-sm" data-testid="fr-nearby-service-links">
          {c.nearby.map((n) => (
            <Link key={n.slug} to={`/fr/villes/${n.slug}/${service.slug}`} data-testid={`fr-nearby-${service.slug}-${n.slug}`}
              className="inline-flex items-center gap-1.5 text-slate-600 transition-colors hover:text-cyan-700">
              {service.short} à {n.name} <ArrowRight className="h-3 w-3 text-slate-300" />
            </Link>
          ))}
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white" data-testid="fr-sc-pricing">
        <div className="mx-auto max-w-6xl px-5 py-14 sm:py-16">
          <Rise><Eyebrow>Tarification</Eyebrow>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Prix du {short} à {city.name}
            </h2></Rise>
          <div className="mt-6 max-w-3xl space-y-5">
            {c.pricing.map((p, i) => <p key={i} className="leading-relaxed text-slate-600">{p}</p>)}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-14 sm:py-16" data-testid="fr-sc-other">
        <Rise><Eyebrow>Autres services à {city.name}</Eyebrow></Rise>
        <div className="mt-8 flex flex-wrap gap-x-5 gap-y-2.5 text-sm" data-testid="fr-other-service-links">
          {c.others.map((s) => (
            <Link key={s.slug} to={`/fr/villes/${city.slug}/${s.slug}`} data-testid={`fr-other-service-${s.slug}`}
              className="inline-flex items-center gap-1.5 text-slate-600 transition-colors hover:text-cyan-700">
              {s.short} à {city.name} <ArrowRight className="h-3 w-3 text-slate-300" />
            </Link>
          ))}
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white" data-testid="fr-sc-faq-section">
        <div className="mx-auto max-w-6xl px-5 py-14 sm:py-16">
          <Rise><Eyebrow>FAQ</Eyebrow>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              {service.name} à {city.name} — FAQ
            </h2></Rise>
          <div className="mt-8 max-w-3xl"><FAQAccordion faqs={c.faqs} testid="fr-sc-faq" /></div>
        </div>
      </section>

      <FrCTABand title={`Réservez votre ${short} à ${city.name}`}
        lead={`Sans formulaire, sans attente. Appelez le ${SITE.phone} ou écrivez à ${SITE.email} — soumission en moins d'une heure ouvrable.`}
        prefix="fr-sc-cta" />
    </div>
  );
}
